import { type SubmitEvent, useId, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import styles from './auth.module.css';
import { useSession } from './SessionProvider';

/** `ProtectedRoute` attaches `{ from: location }` when it redirects here. */
function destinationAfterSignIn(state: unknown): string {
  if (typeof state !== 'object' || state === null || !('from' in state)) {
    return '/';
  }
  const from = (state as { from?: unknown }).from;
  if (typeof from !== 'object' || from === null || !('pathname' in from)) {
    return '/';
  }
  const pathname = (from as { pathname?: unknown }).pathname;
  return typeof pathname === 'string' ? pathname : '/';
}

/** Public sign-in screen. The only unauthenticated route in the app. */
export function LoginScreen() {
  const { status, login, isLoggingIn, loginError, signInReason, clearSignInReason } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Already signed in (a direct visit to /iniciar-sesion, or right after login already
  // resolved): go straight to where the owner was headed instead of showing the form.
  if (status === 'authenticated') {
    return <Navigate to={destinationAfterSignIn(location.state)} replace />;
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      await login({ email, password });
      void navigate(destinationAfterSignIn(location.state), { replace: true });
    } catch {
      // The failure is already captured as `loginError` from the mutation and rendered below.
    }
  }

  return (
    <div className={styles.screen}>
      <h1 className={styles.title}>Inicia sesión</h1>

      {signInReason === 'expired' ? (
        <p className={styles.notice} role="status">
          Tu sesión expiró. Inicia sesión de nuevo para continuar.
        </p>
      ) : null}

      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={emailId}>
            Correo electrónico
          </label>
          <input
            id={emailId}
            className={styles.input}
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearSignInReason();
            }}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={passwordId}>
            Contraseña
          </label>
          <input
            id={passwordId}
            className={styles.input}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearSignInReason();
            }}
          />
        </div>

        {loginError ? (
          <p className={styles.error} role="alert">
            {loginError.message}
          </p>
        ) : null}

        <button className={styles.submit} type="submit" disabled={isLoggingIn}>
          {isLoggingIn ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}
